import React, { useState, useEffect } from 'react';
import {
  UserIcon,
  EyeIcon,
  ArrowRightIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PlusIcon
} from '@heroicons/react/24/outline';

// Services
import { avatarAPI, metadataAPI } from '../services/api';
import GuestConnectPrompt from './GuestConnectPrompt';
import SimulationStatusBanner from './SimulationStatusBanner';

const AvatarView = ({ userAddress }) => {
  const [avatars, setAvatars] = useState([]);
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [supportedStandards, setSupportedStandards] = useState([]);
  const [portabilityPackage, setPortabilityPackage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (userAddress) {
      loadAvatarData();
    } else {
      setLoading(false);
    }
  }, [userAddress]);

  const loadAvatarData = async () => {
    setLoading(true);
    try {
      const [avatarsResponse, standardsResponse] = await Promise.all([
        avatarAPI.getUserAvatars(userAddress),
        avatarAPI.getSupportedStandards()
      ]);

      // Mock avatar data
      setAvatars([
        {
          id: 'avatar_1',
          name: 'Cyber Punk Avatar',
          metaverse: 'Galaxia',
          image: '/api/placeholder/200/200',
          portabilityScore: 85,
          linkedMetaverses: ['Galaxia'],
          attributes: [
            { trait_type: 'Style', value: 'Cyber Punk' },
            { trait_type: 'Rarity', value: 'Epic' },
            { trait_type: 'Universe', value: 'Cyber City' }
          ],
          createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 30), // 30 days ago
          lastUpdated: new Date(Date.now() - 1000 * 60 * 60 * 24 * 7) // 7 days ago
        },
        {
          id: 'avatar_2',
          name: 'Ready Player Me Avatar',
          metaverse: 'Ready Player Me',
          image: '/api/placeholder/200/200',
          portabilityScore: 92,
          linkedMetaverses: ['Galaxia', 'Ready Player Me'],
          attributes: [
            { trait_type: 'Body Type', value: 'Athletic' },
            { trait_type: 'Skin Tone', value: 'Medium' },
            { trait_type: 'Hair Style', value: 'Short' }
          ],
          createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 15), // 15 days ago
          lastUpdated: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2) // 2 days ago
        }
      ]);

      setSupportedStandards(standardsResponse.data);
    } catch (error) {
      console.error('Failed to load avatar data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLinkMetaverse = async (avatarId, metaverseId) => {
    try {
      await avatarAPI.linkAvatarToMetaverse(avatarId, metaverseId, {});
      loadAvatarData(); // Refresh data
    } catch (error) {
      console.error('Failed to link avatar to metaverse:', error);
    }
  };

  const handleGeneratePortabilityPackage = async (avatarId) => {
    try {
      const response = await avatarAPI.generatePortabilityPackage(avatarId, 'ready_player_me');
      setPortabilityPackage(response.data);
    } catch (error) {
      console.error('Failed to generate portability package:', error);
    }
  };

  const getPortabilityScoreColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-100';
    if (score >= 60) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;

    if (diff < 1000 * 60 * 60) return `${Math.floor(diff / (1000 * 60))} minutes ago`;
    if (diff < 1000 * 60 * 60 * 24) return `${Math.floor(diff / (1000 * 60 * 60))} hours ago`;
    return `${Math.floor(diff / (1000 * 60 * 60 * 24))} days ago`;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="galaxia-card p-8 animate-pulse h-32" />
      </div>
    );
  }

  if (!userAddress) {
    return (
      <div className="space-y-6">
        <SimulationStatusBanner />
        <GuestConnectPrompt
          title="Manage avatar portability"
          description="Connect a wallet to register, link, and port avatars across Galaxia Native, Ready Player Me, and OMA3."
          primaryTo="/bridge"
          primaryLabel="Explore Bridge"
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="galaxia-card p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-galaxia">Avatar Management</h1>
            <p className="text-galaxia-muted mt-1">
              Manage your avatar identity across multiple metaverses
            </p>
          </div>
          <button className="galaxia-btn-primary px-4 py-2 text-sm font-medium">
            <PlusIcon className="h-5 w-5 inline mr-2" />
            Create Avatar
          </button>
        </div>
      </div>

      {/* Avatars Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {avatars.map((avatar) => (
          <div
            key={avatar.id}
            className="galaxia-card border border-[var(--galaxia-border)] overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => setSelectedAvatar(avatar)}
          >
            <div className="aspect-w-1 aspect-h-1">
              <img
                src={avatar.image}
                alt={avatar.name}
                className="w-full h-48 object-cover"
              />
            </div>
            <div className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-galaxia truncate">
                    {avatar.name}
                  </h3>
                  <p className="text-xs text-galaxia-muted mt-1">
                    {avatar.metaverse}
                  </p>
                </div>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPortabilityScoreColor(avatar.portabilityScore)}`}>
                  {avatar.portabilityScore}% Portable
                </span>
              </div>

              <div className="mt-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-galaxia-muted">Linked Metaverses:</span>
                  <span className="font-medium text-galaxia">
                    {avatar.linkedMetaverses.length}
                  </span>
                </div>
                <div className="mt-2 text-xs text-galaxia-muted">
                  Updated {formatTimeAgo(avatar.lastUpdated)}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {avatars.length === 0 && (
        <div className="text-center py-12">
          <UserIcon className="mx-auto h-12 w-12 text-galaxia-muted" />
          <h3 className="mt-2 text-sm font-medium text-galaxia">No avatars found</h3>
          <p className="mt-1 text-sm text-galaxia-muted">
            Create your first avatar to start using it across metaverses
          </p>
          <div className="mt-6">
            <button className="galaxia-btn-primary inline-flex items-center px-4 py-2 text-sm font-medium">
              <PlusIcon className="h-5 w-5 mr-2" />
              Create Avatar
            </button>
          </div>
        </div>
      )}

      {/* Avatar Detail Modal */}
      {selectedAvatar && (
        <div className="fixed inset-0 overflow-y-auto h-full w-full z-50" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <div className="relative top-20 mx-auto p-5 w-full max-w-4xl galaxia-card shadow-lg">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center">
                <img
                  src={selectedAvatar.image}
                  alt={selectedAvatar.name}
                  className="w-20 h-20 object-cover rounded-lg mr-4"
                />
                <div>
                  <h3 className="text-xl font-medium text-galaxia">
                    {selectedAvatar.name}
                  </h3>
                  <p className="text-sm text-galaxia-muted">
                    {selectedAvatar.metaverse} • Portability Score: {selectedAvatar.portabilityScore}%
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSelectedAvatar(null)}
                className="text-galaxia-muted hover:text-galaxia-muted"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Avatar Attributes */}
              <div>
                <h4 className="text-lg font-medium text-galaxia mb-4">Avatar Attributes</h4>
                <div className="space-y-3">
                  {selectedAvatar.attributes.map((attr, index) => (
                    <div key={index} className="flex justify-between py-2 border-b border-galaxia">
                      <span className="text-sm text-galaxia-muted">{attr.trait_type}</span>
                      <span className="text-sm font-medium text-galaxia">{attr.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Linked Metaverses */}
              <div>
                <h4 className="text-lg font-medium text-galaxia mb-4">Linked Metaverses</h4>
                <div className="space-y-3">
                  {selectedAvatar.linkedMetaverses.map((metaverse, index) => (
                    <div key={index} className="flex items-center justify-between p-3 rounded-md">
                      <span className="text-sm font-medium text-galaxia">{metaverse}</span>
                      <CheckCircleIcon className="h-5 w-5" style={{ color: 'var(--galaxia-success)' }} />
                    </div>
                  ))}
                </div>

                {/* Link to New Metaverse */}
                <div className="mt-4">
                  <select className="galaxia-input galaxia-select w-full">
                    <option value="">Link to another metaverse...</option>
                    {supportedStandards
                      .filter(standard => !selectedAvatar.linkedMetaverses.includes(standard.id))
                      .map(standard => (
                        <option key={standard.id} value={standard.id}>
                          {standard.name}
                        </option>
                      ))
                    }
                  </select>
                </div>
              </div>
            </div>

            {/* Portability Actions */}
            <div className="mt-6 pt-6 border-t border-galaxia">
              <h4 className="text-lg font-medium text-galaxia mb-4">Portability Actions</h4>
              <div className="flex space-x-3">
                <button
                  onClick={() => handleGeneratePortabilityPackage(selectedAvatar.id)}
                  className="flex-1 galaxia-btn-primary px-4 py-2 text-sm font-medium"
                >
                  Generate Portability Package
                </button>
                <button className="flex-1 galaxia-btn-primary px-4 py-2 text-sm font-medium">
                  Update Avatar
                </button>
                <button className="flex-1 galaxia-btn-secondary px-4 py-2 text-sm font-medium">
                  View History
                </button>
              </div>
            </div>

            {/* Portability Package Display */}
            {portabilityPackage && (
              <div className="mt-6 pt-6 border-t border-galaxia">
                <h4 className="text-lg font-medium text-galaxia mb-4">Portability Package</h4>
                <div className="rounded-md p-4" style={{ background: 'var(--galaxia-surface)' }}>
                  <pre className="text-xs text-galaxia overflow-x-auto">
                    {JSON.stringify(portabilityPackage, null, 2)}
                  </pre>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Supported Standards */}
      <div className="galaxia-card p-6">
        <h3 className="text-lg font-semibold text-galaxia mb-4">Supported Avatar Standards</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {supportedStandards.map((standard) => (
            <div key={standard.id} className="border border-[var(--galaxia-border)] rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-galaxia">{standard.name}</h4>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  standard.compatibility === 'high'
                    ? 'bg-green-100 text-green-800'
                    : standard.compatibility === 'native'
                    ? 'galaxia-status-muted'
                    : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {standard.compatibility}
                </span>
              </div>
              <p className="text-xs text-galaxia-muted mb-3">{standard.description}</p>
              <div className="text-xs text-galaxia-muted">
                Features: {standard.features.join(', ')}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AvatarView;
